import React, { useEffect, useState } from 'react';

const ScannerDiagnostic = () => {
  const [diagnostics, setDiagnostics] = useState({});

  useEffect(() => {
    const runDiagnostics = async () => {
      const results = {};
      
      // Check browser support
      const userAgent = navigator.userAgent;
      const isIOS = /iPad|iPhone|iPod/.test(userAgent);
      const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent);
      
      results.browserSupport = {
        getUserMedia: !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia),
        webkitGetUserMedia: !!navigator.webkitGetUserMedia, // Legacy webkit support
        webRTC: !!window.RTCPeerConnection,
        https: window.location.protocol === 'https:' || window.location.hostname === 'localhost',
        hostname: window.location.hostname,
        protocol: window.location.protocol,
        userAgent: userAgent,
        isIOS: isIOS,
        isSafari: isSafari,
        isSecureContext: window.isSecureContext
      };

      // Check camera permissions
      if (navigator.permissions) {
        try {
          const permission = await navigator.permissions.query({ name: 'camera' });
          results.cameraPermission = {
            state: permission.state,
            supported: true
          };
        } catch (error) {
          results.cameraPermission = {
            error: error.message,
            supported: false
          };
        }
      } else {
        results.cameraPermission = {
          error: 'Permissions API not supported',
          supported: false
        };
      }

      // Try to enumerate media devices
      if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
        try {
          const devices = await navigator.mediaDevices.enumerateDevices();
          const cameras = devices.filter(device => device.kind === 'videoinput');
          results.cameras = {
            total: cameras.length,
            devices: cameras.map(camera => ({
              id: camera.deviceId,
              label: camera.label || 'Unknown Camera',
              groupId: camera.groupId
            }))
          };
        } catch (error) {
          results.cameras = {
            error: error.message
          };
        }
      }

      // Test basic camera access
      if (results.browserSupport.getUserMedia) {
        try {
          // For Safari/iOS, use more specific constraints
          const constraints = {
            video: {
              facingMode: 'environment',
              width: { min: 320, ideal: 640, max: 1280 },
              height: { min: 240, ideal: 480, max: 720 }
            }
          };
          
          const stream = await navigator.mediaDevices.getUserMedia(constraints);
          results.cameraAccess = {
            success: true,
            streamActive: stream.active,
            videoTracks: stream.getVideoTracks().length,
            videoTrackSettings: stream.getVideoTracks()[0]?.getSettings() || {}
          };
          // Stop the stream immediately
          stream.getTracks().forEach(track => track.stop());
        } catch (error) {
          // If environment camera fails, try any camera
          try {
            const fallbackStream = await navigator.mediaDevices.getUserMedia({ video: true });
            results.cameraAccess = {
              success: true,
              fallbackUsed: true,
              streamActive: fallbackStream.active,
              videoTracks: fallbackStream.getVideoTracks().length,
              videoTrackSettings: fallbackStream.getVideoTracks()[0]?.getSettings() || {}
            };
            fallbackStream.getTracks().forEach(track => track.stop());
          } catch (fallbackError) {
            results.cameraAccess = {
              error: `Primary: ${error.message}, Fallback: ${fallbackError.message}`,
              errorName: fallbackError.name,
              primaryErrorName: error.name
            };
          }
        }
      } else {
        results.cameraAccess = {
          error: 'getUserMedia not supported'
        };
      }

      // Check for QR scanner libraries
      results.libraries = {};
      
      try {
        const Html5Qrcode = await import('html5-qrcode');
        results.libraries.html5Qrcode = {
          loaded: true,
          version: 'Available'
        };
      } catch (error) {
        results.libraries.html5Qrcode = {
          loaded: false,
          error: error.message
        };
      }

      try {
        const QrScanner = await import('qr-scanner');
        results.libraries.qrScanner = {
          loaded: true,
          workerPath: QrScanner.default.WORKER_PATH
        };
      } catch (error) {
        results.libraries.qrScanner = {
          loaded: false,
          error: error.message
        };
      }

      setDiagnostics(results);
    };

    runDiagnostics();
  }, []);

  return (
    <div className="p-6 bg-white shadow rounded-lg max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold mb-6">QR Scanner Diagnostics</h2>
      
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold mb-3">Browser Support</h3>
          <div className="bg-gray-50 p-4 rounded">
            <pre className="text-sm">{JSON.stringify(diagnostics.browserSupport, null, 2)}</pre>
          </div>
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-3">Camera Permission</h3>
          <div className="bg-gray-50 p-4 rounded">
            <pre className="text-sm">{JSON.stringify(diagnostics.cameraPermission, null, 2)}</pre>
          </div>
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-3">Available Cameras</h3>
          <div className="bg-gray-50 p-4 rounded">
            <pre className="text-sm">{JSON.stringify(diagnostics.cameras, null, 2)}</pre>
          </div>
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-3">Camera Access Test</h3>
          <div className="bg-gray-50 p-4 rounded">
            <pre className="text-sm">{JSON.stringify(diagnostics.cameraAccess, null, 2)}</pre>
          </div>
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-3">QR Libraries</h3>
          <div className="bg-gray-50 p-4 rounded">
            <pre className="text-sm">{JSON.stringify(diagnostics.libraries, null, 2)}</pre>
          </div>
        </div>
      </div>

      <div className="mt-8">
        <h3 className="text-lg font-semibold mb-3">Console Logs</h3>
        <div className="bg-gray-800 text-green-400 p-4 rounded text-sm font-mono">
          <div>Check browser console (F12) for additional logs and errors.</div>
          <div>Common issues:</div>
          <div>• Camera permission denied</div>
          <div>• HTTPS required for camera access</div>
          <div>• Camera in use by another application</div>
          <div>• Library loading failures</div>
        </div>
      </div>
    </div>
  );
};

export default ScannerDiagnostic;
